import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";

import { ReactComponent as LeftArrow } from "../../resources/icons/left_arrow.svg";
import { ReactComponent as AssignorIcon } from "../../resources/icons/assignor.svg";
import { ReactComponent as HelmetIcon } from "../../resources/icons/helmet.svg";
import { ReactComponent as CalendarCheck } from "../../resources/icons/calendar_check.svg";
import { ReactComponent as ClipboardCheck } from "../../resources/icons/clipboard_check.svg";
import { ReactComponent as ClockCheck } from "../../resources/icons/clock_check.svg";
import { ReactComponent as CoinsStacked } from "../../resources/icons/coins_stacked.svg";
import ProjectDataSection from "../elements/ProjectDataSection";

ChartJS.register(ArcElement, Tooltip, Legend);
const generateShades = (base: string, count: number): string[] => {
  const hexToHSL = (H: string) => {
    let r = 0,
      g = 0,
      b = 0;
    if (H.length === 7) {
      r = parseInt(H.slice(1, 3), 16) / 255;
      g = parseInt(H.slice(3, 5), 16) / 255;
      b = parseInt(H.slice(5, 7), 16) / 255;
    }

    const max = Math.max(r, g, b),
      min = Math.min(r, g, b);
    let h = 0,
      s = 0,
      l = (max + min) / 2;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        case b:
          h = (r - g) / d + 4;
          break;
      }
      h /= 6;
    }

    return { h: h * 360, s: s * 100, l: l * 100 };
  };

  const hsl = hexToHSL(base);
  const minLightness = 25;
  const maxLightness = 88;
  const step = (maxLightness - minLightness) / Math.max(count - 1, 1);

  return Array.from({ length: count }, (_, i) => {
    const l = minLightness + i * step;
    return `hsl(${hsl.h.toFixed(0)}, ${hsl.s.toFixed(0)}%, ${l.toFixed(0)}%)`;
  });
};

const AssignorStatistics: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [project, setProject] = useState<any>(null);
  const [stats, setStats] = useState<any>(null);

  useEffect(() => {
    const fetchStats = async () => {
      const token = localStorage.getItem("token");
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_URL}/admin/completed-projects/${id}/statistics`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "X-Client-Type": "web",
            },
          }
        );
        setProject(res.data.project);
        setStats(res.data.statistics);
      } catch {}
    };
    fetchStats();
  }, [id]);

  if (!project || !stats) return <div>Loading...</div>;

  const sortedTime = [...stats.timeAllocation].sort(
    (a, b) => b.value - a.value
  );
  const sortedBudget = [...stats.budgetAllocation].sort(
    (a, b) => b.value - a.value
  );

  const timeColors = generateShades("#9A3412", sortedTime.length);
  const budgetColors = generateShades("#1D4ED8", sortedBudget.length);

  const hasTimeData = sortedTime.some((t: any) => t.value > 0);
  const hasBudgetData = sortedBudget.some((b: any) => b.value > 0);

  const timeData = {
    labels: sortedTime.map((t: any) => t.name),
    datasets: [
      {
        label: "Hours",
        data: sortedTime.map((t: any) => t.value),
        backgroundColor: timeColors,
      },
    ],
  };

  const budgetData = {
    labels: sortedBudget.map((b: any) => b.name),
    datasets: [
      {
        label: "BGN",
        data: sortedBudget.map((b: any) => b.value),
        backgroundColor: budgetColors,
      },
    ],
  };

  const legendStyle = "font-inter text-[14px] leading-[20px] font-normal";

  return (
    <div className="flex">
      <div className="flex-1 p-6 ml-[17.5rem] ">
        <div className="flex items-center mb-4 border-b pb-4">
          <button
            onClick={() => (location.key ? navigate(-1) : navigate("/"))}
            className="mr-3"
          >
            <LeftArrow className="w-6 h-6" style={{ color: "#344054" }} />
          </button>
          <h1 className="text-2xl font-bold">{project.title}</h1>
          <span
            className={`ml-4 text-sm font-medium px-3 py-1 border rounded-full text-[#067647] bg-[#ECFDF3] border-[#ABEFC6]`}
          >
            {project.status[0].toUpperCase() +
              project.status.slice(1).toLowerCase()}
          </span>
        </div>

        <div className="flex justify-between gap-6">
          <div className="flex-1 max-w-[44.7rem] space-y-8">
            <div>
              <h2 className="text-lg font-semibold mb-1">
                Project details summary
              </h2>
              <p className="max-w-[500px] max-h-32 overflow-auto pr-2 whitespace-pre-wrap break-words text-[#475467]">
                {project.summary}
              </p>
            </div>

            <div className="flex lg:flex-row flex-col lg:gap-10 gap-5 w-full">
              <div className="flex flex-col w-fit gap-1">
                <p className="text-sm font-semibold text-[#344054]">Assignor</p>
                <div className="flex items-center gap-1">
                  <AssignorIcon className="w-6 h-4 -ml-1" />
                  <span className="text-sm text-[#101828] font-light max-w-[10rem] text-wrap text-start">
                    {project.assignor?.name || "—"}
                  </span>
                </div>
              </div>

              <div className="flex flex-col w-fit gap-1">
                <p className="text-sm font-semibold text-[#344054]">
                  Contractor
                </p>
                <div className="flex items-center gap-1">
                  <HelmetIcon className="w-6 h-4 -ml-1" />
                  <span className="text-sm text-[#101828] font-light max-w-[10rem] text-wrap text-start">
                    {project.contractor?.name || "—"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <ProjectDataSection project={project} />

          {/* <div className="grid grid-cols-2  text-sm bg-[#F9FAFB] rounded-lg p-6 w-[22.56rem] ">
            <div className="flex gap-2 mb-2">
              <div>
                <p className="text-[#98A2B3] mb-2">Published date</p>
                <div className="flex flex-row">
                  <CalendarIcon className="w-4 h-4 mr-1" />
                  <p className="text-[#101828]">
                    {project.publishedAt?.slice(0, 10)}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-2 mb-2">
              <div>
                <p className="text-[#98A2B3] mb-2">Completion date</p>
                <div className="flex flex-row">
                  <CalendarCheck className="w-4 h-4 mr-1" />
                  <p className="text-[#101828]">
                    {project.publishedAt?.slice(0, 10)}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-2 mb-2">
              <div>
                <p className="text-[#98A2B3] mb-2">Execution plan</p>
                <div className="flex flex-row">
                  <Hourglass className="w-4 h-4 mr-1" />
                  <p className="text-[#101828]">
                    {project.executionMonths} months
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-2 mb-2">
              <div>
                <p className="text-[#98A2B3] mb-2">Location</p>
                <div className="flex flex-row">
                  <Location className="w-4 h-4 mr-1" />
                  <p
                    className="text-[#101828] truncate w-[6rem]"
                    title={project.address}
                  >
                    {project.address}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-2 mb-2">
              <div>
                <p className="text-[#98A2B3] mb-2">Square meters</p>
                <div className="flex flex-row">
                  <Squared className="w-4 h-4 mr-1" />
                  <p
                    className="text-[#101828] truncate w-[6rem]"
                    title={project.squareMeters}
                  >
                    {project.squareMeters}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-2 mb-2">
              <div>
                <p className="text-[#98A2B3]  mb-2">Rooms</p>
                <div className="flex flex-row">
                  <Rooms className="w-4 h-4 mr-1" />
                  <p
                    className="text-[#101828 ] truncate w-[6rem]"
                    title={project.rooms}
                  >
                    {project.rooms}
                  </p>
                </div>
              </div>
            </div>
          </div> */}
        </div>

        <div className="grid grid-cols-4 gap-4 mt-10">
          {[
            {
              label: "Total construction activities",
              value: stats.totalActivities,
              Icon: ClipboardCheck,
            },
            {
              label: "Total hours (Across all tasks)",
              value: stats.totalHours,
              Icon: ClockCheck,
            },
            {
              label: "Work days (8-hour days)",
              value: `${stats.totalWorkDays} days`,
              Icon: CalendarCheck,
            },
            {
              label: "Total budget",
              value: `${stats.totalBudget.toLocaleString()} BGN`,
              Icon: CoinsStacked,
            },
          ].map(({ label, value, Icon }, i) => (
            <div
              key={i}
              className="bg-[#F9FAFB] p-4 border border-[#EAECF0] rounded-lg"
            >
              <Icon className="w-5 h-5 mb-2 text-[#667085]" />
              <p className="text-sm text-[#98A2B3]">{label}</p>
              <p className="text-xl font-bold text-[#101828]">{value}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-8 mt-4">
          {hasTimeData && (
            <div>
              <h3 className="text-lg font-semibold mb-2">Time allocation</h3>
              <div style={{ width: 240, height: 240 }}>
                <Pie
                  data={timeData}
                  options={{
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        callbacks: {
                          // Заглавие: превежда го на нови редове
                          title: function (tooltipItems: any) {
                            const label = tooltipItems?.[0]?.label || "";
                            const maxLineLength = 28;
                            return (
                              label.match(
                                new RegExp(`.{1,${maxLineLength}}`, "g")
                              ) || []
                            );
                          },
                          // Label: само цветно квадратче и стойността
                          label: function (tooltipItem: any) {
                            const value = tooltipItem.raw || 0;
                            return [` Hours: ${value}`];
                          },
                        },
                        usePointStyle: false,
                      },
                    },
                  }}
                />
              </div>
              <div className="mt-4 space-y-2">
                {sortedTime.map((t: any, i: number) => (
                  <div key={t.id} className={legendStyle}>
                    <span
                      className="inline-block w-3 h-3 mr-2 rounded-sm"
                      style={{ backgroundColor: timeColors[i] }}
                    ></span>
                    {t.name} — {t.value} hrs ({t.percent.toFixed(2)}%)
                  </div>
                ))}
              </div>
            </div>
          )}
          {hasBudgetData && (
            <div>
              <h3 className="text-lg font-semibold mb-2">Budget allocation</h3>
              <div style={{ width: 240, height: 240 }}>
                <Pie
                  data={budgetData}
                  options={{
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        callbacks: {
                          // Заглавие: превежда го на нови редове
                          title: function (tooltipItems: any) {
                            const label = tooltipItems?.[0]?.label || "";
                            const maxLineLength = 28;
                            return (
                              label.match(
                                new RegExp(`.{1,${maxLineLength}}`, "g")
                              ) || []
                            );
                          },
                          // Label: само цветно квадратче и стойността
                          label: function (tooltipItem: any) {
                            const value = tooltipItem.raw || 0;
                            return [` Hours: ${value}`];
                          },
                        },
                        usePointStyle: false,
                      },
                    },
                  }}
                />
              </div>
              <div className="mt-4 space-y-2">
                {sortedBudget.map((b: any, i: number) => (
                  <div key={b.id} className={legendStyle}>
                    <span
                      className="inline-block w-3 h-3 mr-2 rounded-sm"
                      style={{ backgroundColor: budgetColors[i] }}
                    ></span>
                    {b.name} — {b.value.toLocaleString()} BGN (
                    {b.percent.toFixed(2)}%)
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssignorStatistics;
